import MenuItemModel from '../models/nlp/menu/MenuItemModel';
import { MenuItemOption } from '../models/nlp/menu/MenuItemOption';
import { MenuItemOptionGroup } from '../models/nlp/menu/MenuItemOptionGroup';

export interface DuplicateSpokenName {
    groupName: string;
    spokenName: string;
    optionNames: string[];
}

// The name the assistant says and listens for: the "Referred to As" name, falling back to the POS name.
export function getSpokenOptionName(option: MenuItemOption): string {
    return (option?.colloquialName?.trim() || option?.name?.trim() || '');
}

const normalize = (name: string) => (name ?? '').trim().toLowerCase();

// One group where several enabled options share a spoken name.
// path identifies where the group sits in the item (size, parent option), since the same group repeats under each size.
interface SpokenNameCollision {
    path: string;
    group: MenuItemOptionGroup;
    spokenKey: string;
    options: MenuItemOption[];
}

function collectEnabledOptionGroups(groups: MenuItemOptionGroup[], parentPath: string, result: { path: string, group: MenuItemOptionGroup }[]) {
    for (const group of groups ?? []) {
        if (!group || group.isDisabled) continue;
        const path = `${parentPath}/g:${group.referenceId || normalize(group.name)}`;
        result.push({ path, group });
        for (const option of group.options ?? []) {
            if (!option || option.isDisabled) continue;
            collectEnabledOptionGroups(option.optionGroups, `${path}/o:${option.id}`, result);
        }
    }
}

function findCollisions(menuItem: MenuItemModel): SpokenNameCollision[] {
    const groups: { path: string, group: MenuItemOptionGroup }[] = [];
    collectEnabledOptionGroups(menuItem?.optionGroups, '', groups);
    for (const sizeGroup of menuItem?.sizeGroups ?? []) {
        if (!sizeGroup || sizeGroup.isDisabled) continue;
        for (const size of sizeGroup.sizes ?? []) {
            if (!size || size.isDisabled) continue;
            collectEnabledOptionGroups(size.optionGroups, `s:${size.id}`, groups);
        }
    }

    const collisions: SpokenNameCollision[] = [];
    for (const { path, group } of groups) {
        const optionsBySpokenName = new Map<string, MenuItemOption[]>();
        for (const option of group.options ?? []) {
            if (!option || option.isDisabled) continue;
            const spokenKey = normalize(getSpokenOptionName(option));
            if (!spokenKey) continue;
            optionsBySpokenName.set(spokenKey, [...(optionsBySpokenName.get(spokenKey) ?? []), option]);
        }
        optionsBySpokenName.forEach((options, spokenKey) => {
            if (new Set(options.map(o => o.id)).size < 2) return;
            collisions.push({ path, group, spokenKey, options });
        });
    }
    return collisions;
}

// Every pair of different options that clash, so adding a third option to a clash counts as new
// but removing one from a three-way clash doesn't.
function collisionPairKeys(collision: SpokenNameCollision): string[] {
    const ids = Array.from(new Set(collision.options.map(o => o.id))).sort();
    const keys: string[] = [];
    for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) {
            keys.push(`${collision.path}|${collision.spokenKey}|${ids[i]}|${ids[j]}`);
        }
    }
    return keys;
}

// Lists each clash once for display, merging the copies of a group repeated under each size.
function toDisplay(collisions: SpokenNameCollision[]): DuplicateSpokenName[] {
    const byKey = new Map<string, DuplicateSpokenName>();
    for (const collision of collisions) {
        const key = `${normalize(collision.group.name)}|${collision.spokenKey}`;
        const existing = byKey.get(key);
        const optionNames = collision.options.map(o => o.name);
        if (existing) {
            for (const name of optionNames) {
                if (!existing.optionNames.includes(name)) existing.optionNames.push(name);
            }
        } else {
            byKey.set(key, {
                groupName: collision.group.name,
                spokenName: getSpokenOptionName(collision.options[0]),
                optionNames
            });
        }
    }
    return Array.from(byKey.values());
}

/**
 * Finds option groups where two different enabled options share the same spoken name.
 * The assistant can't tell those options apart, so it may send the wrong one to the POS
 * (e.g. Banana Peppers renamed to "50-50 Cheese Blend" next to the real 50-50 option).
 */
export function findDuplicateSpokenOptionNames(menuItem: MenuItemModel): DuplicateSpokenName[] {
    return toDisplay(findCollisions(menuItem));
}

/**
 * Finds duplicates across a whole menu, listing each duplicate once with the items it appears on.
 */
export function findDuplicateSpokenOptionNamesInMenu(menuItems: MenuItemModel[]): (DuplicateSpokenName & { itemNames: string[] })[] {
    const byKey = new Map<string, DuplicateSpokenName & { itemNames: string[] }>();
    for (const menuItem of menuItems ?? []) {
        if (!menuItem || menuItem.isDisabled) continue;
        for (const duplicate of findDuplicateSpokenOptionNames(menuItem)) {
            const key = duplicateKey(duplicate);
            const existing = byKey.get(key);
            if (existing) {
                if (!existing.itemNames.includes(menuItem.originalName)) existing.itemNames.push(menuItem.originalName);
            } else {
                byKey.set(key, { ...duplicate, itemNames: [menuItem.originalName] });
            }
        }
    }
    return Array.from(byKey.values());
}

const duplicateKey = (d: DuplicateSpokenName) =>
    `${normalize(d.groupName)}|${normalize(d.spokenName)}|${d.optionNames.map(normalize).sort().join(',')}`;

/**
 * Returns the duplicates in the edited item that weren't already in the saved item,
 * so existing duplicates don't trigger a warning on every save.
 */
export function findNewDuplicateSpokenOptionNames(original: MenuItemModel, edited: MenuItemModel): DuplicateSpokenName[] {
    const existingPairs = new Set(findCollisions(original).flatMap(collisionPairKeys));
    const newCollisions = findCollisions(edited).filter(c => collisionPairKeys(c).some(key => !existingPairs.has(key)));
    return toDisplay(newCollisions);
}

/**
 * Like findNewDuplicateSpokenOptionNames, for a change applied to several items at once.
 */
export function findNewDuplicateSpokenOptionNamesInItems(items: { original: MenuItemModel, edited: MenuItemModel }[]): (DuplicateSpokenName & { itemNames: string[] })[] {
    const byKey = new Map<string, DuplicateSpokenName & { itemNames: string[] }>();
    for (const { original, edited } of items ?? []) {
        if (!edited) continue;
        for (const duplicate of findNewDuplicateSpokenOptionNames(original, edited)) {
            const key = duplicateKey(duplicate);
            const existing = byKey.get(key);
            if (existing) {
                if (!existing.itemNames.includes(edited.originalName)) existing.itemNames.push(edited.originalName);
            } else {
                byKey.set(key, { ...duplicate, itemNames: [edited.originalName] });
            }
        }
    }
    return Array.from(byKey.values());
}
